'use client';

import { faShareNodes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import { Credenza, CredenzaContent } from '@pickleballinc/ui/components/ui/credenza';
import clsx from 'clsx';

import ImageWithFallback from '@/components/ImageWithFallback/ImageWithFallback';
import { ConfettiBg } from '@/modules/players/v3/components/player-pages/achievements/svgs/confetti-bg';
import { BragButton } from '@/modules/players/v3/components/shared/brag/brag-button';
import { VerticalMilestoneBar } from '@/modules/players/v3/components/shared/milestone-bar/vertical-milestone-bar';
import {
  ACHIEVEMENT_DETAIL_MODAL_CONTENT_CLASSNAME,
  ACHIEVEMENT_DETAIL_MODAL_HEADER_GRADIENT_ACTIVE,
  ACHIEVEMENT_DETAIL_MODAL_HEADER_GRADIENT_NOT_STARTED,
  ACHIEVEMENT_SHARE_LABEL,
} from '@/modules/players/v3/constants/achievements';
import type { Achievement } from '@/modules/players/v3/types/achievements';
import { getNormalizedAchievementMilestones } from '@/modules/players/v3/utils/achievement';
import {
  getAchievementDetailModalDescription,
  isAchievementDetailNotStarted,
} from '@/modules/players/v3/utils/achievement-detail-modal';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';

interface AchievementDetailModalProps {
  achievement: Achievement;
  open: boolean;
  onClose: () => void;
  onShare?: () => void;
  showShare?: boolean;
  showBrag?: boolean;
}

export function AchievementDetailModal({
  achievement,
  open,
  onClose,
  onShare,
  showShare = true,
  showBrag = showShare,
}: AchievementDetailModalProps) {
  const isCompleted = achievement.alreadyAchieved;
  const isNotStarted = isAchievementDetailNotStarted(achievement);
  const description = getAchievementDetailModalDescription(achievement);
  const milestones = getNormalizedAchievementMilestones(achievement);

  const achievedCount = milestones.filter((m) => m.achieved).length;
  const totalMilestones = milestones.length;

  return (
    <Credenza
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <CredenzaContent
        className={ACHIEVEMENT_DETAIL_MODAL_CONTENT_CLASSNAME}
        closeButtonColor={isNotStarted ? undefined : 'white'}
        dialogTitle={achievement.achievementName}
        overlayHandle
      >
        <div
          className='relative flex flex-col items-center px-6 pt-10 pb-5 overflow-hidden'
          style={{
            background: isNotStarted
              ? ACHIEVEMENT_DETAIL_MODAL_HEADER_GRADIENT_NOT_STARTED
              : ACHIEVEMENT_DETAIL_MODAL_HEADER_GRADIENT_ACTIVE,
          }}
        >
          {isCompleted ? (
            <ConfettiBg className='absolute top-0 left-0 w-full pointer-events-none' />
          ) : null}

          <div className='relative z-10 size-[136px] rounded-xl bg-white border border-gray-200 p-2 overflow-hidden'>
            <ImageWithFallback
              src={achievement.logo ?? undefined}
              fallbackSrc={PICKLEBALL_PLACEHOLDER_IMAGE}
              alt={achievement.achievementName}
              className={clsx('size-full object-contain rounded-lg', isNotStarted && 'grayscale')}
            />
          </div>

          <div className='relative z-10 flex flex-col items-center gap-1 pt-3'>
            <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#f8f9fc] border border-[#d5d9eb] text-[#363f72]'>
              {achievement.categoryName}
            </span>
            <p
              className={clsx(
                'text-lg font-semibold text-center leading-7 mt-0.5',
                isNotStarted ? 'text-gray-900' : 'text-white'
              )}
            >
              {achievement.achievementName}
            </p>
            <p
              className={clsx(
                'text-sm text-center leading-5',
                isNotStarted ? 'text-gray-600' : 'text-white/90'
              )}
            >
              {description}
            </p>
          </div>
        </div>

        <div className='flex flex-col gap-4 px-4 pt-5 pb-2'>
          <div className='flex items-center gap-2'>
            <span className='text-base font-semibold text-gray-900'>Current progress</span>
            {isCompleted ? (
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-50 border border-success-200 text-success-700'>
                Completed
              </span>
            ) : (
              <span
                className={clsx(
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border',
                  isNotStarted
                    ? 'bg-gray-100 border-gray-200 text-gray-500'
                    : 'bg-blue-50 border-blue-200 text-blue-700'
                )}
              >
                {`${achievedCount} / ${totalMilestones}`}
              </span>
            )}
          </div>

          <div className='max-h-[30dvh] overflow-y-auto md:max-h-none md:overflow-visible'>
            <VerticalMilestoneBar milestones={milestones} isCompleted={isCompleted} />
          </div>
        </div>

        <div className='flex flex-col items-center gap-4 px-4 pt-2 pb-4'>
          {isCompleted && (showShare || showBrag) ? (
            <div className='flex w-full gap-3'>
              {showShare ? (
                <Button
                  className='flex-1 justify-center gap-2 !bg-brand !text-white'
                  onClick={() => {
                    onClose();
                    onShare?.();
                  }}
                >
                  <FontAwesomeIcon icon={faShareNodes} className='size-4' />
                  {ACHIEVEMENT_SHARE_LABEL}
                </Button>
              ) : null}
              {showBrag ? (
                <BragButton
                  className='flex-1'
                  achievementId={achievement.achievementId}
                  achievementName={achievement.achievementName}
                  showIcon
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </CredenzaContent>
    </Credenza>
  );
}
